import React, { useEffect, useRef, useState } from 'react';
import './styles.scss';
import PortalCard from './card';
import ToolBar from '../../components/toolbar';
import CustomCheckBox from '../../components/customCheckBox';
import Loading from '../../components/loading';
import { AutoSizer, Grid, InfiniteLoader } from 'react-virtualized';
import { computeColumnCount } from '../../utils/project';
import paginationUtils from '../../utils/pagination';
import API from '../../services/api';
import utils from '../../utils';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getUser } from '../../redux/user/actions';
import { startPortalPreview } from '../../redux/portal/actions';
import { useDebouncedCallback } from 'use-debounce';
import { createSections } from './createSections/createSections';
import { setAlert, setLoading } from '../../redux/dashboard/actions';
import { getSelectedCompany } from '../../redux/company/actions';
import Wizard from '../../components/wizard';
import ManagePortalModal from './modal/managePortalModal';
import CustomConfirm from '../../components/customConfirm';

const CARD_OUTER_PADDING = 24; // Space between cards
const CONTAINER_OUTER_ELEMENTS_HEIGHT = 183; // Everything out of container
const ROW_HEIGHT = 190; // Grid row height
const LOADING = 1;
const LOADED = 2;
const BATCH_ROW_SIZE = 10;

const PortalsContainer = ({ lastUpdated }) => {
  const [totalPortals, setTotalPortals] = useState(0);
  const [portals, setPortals] = useState(null);
  const [selectedPortal, setSelectedPortal] = useState(null);
  const [newPortal, setNewPortal] = useState({});

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMoreContent, setIsLoadingMoreContent] = useState(false);
  const [ownerFilter, setOwnerFilter] = useState('none');
  const [nextPageToken, setNextPageToken] = useState('');
  const [searchBy, setSearchBy] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [showModal, setShowModal] = useState(true);
  const [currentFilters, setCurrentFilters] = useState('');
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const itemStatusMap = useRef({});
  const company = useSelector(getSelectedCompany);
  const user = useSelector(getUser);
  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    itemStatusMap.current = {};
    // Only fetch portals after user is loaded to avoid accessing user.id too early
    if (user && user.id) {
      loadMoreItems(0, BATCH_ROW_SIZE - 1, true);
      setIsLoading(true);
    } else {
      // Ensure we don't show stale data while user is loading
      setPortals(null);
    }
  }, [searchBy, ownerFilter, lastUpdated, user]);

  useEffect(() => {
    setShowModal(true);
  }, [portals]);

  const handleUpdateInfo = () => {
    // Refresh list with updated info
    itemStatusMap.current = {};
    loadMoreItems(0, BATCH_ROW_SIZE - 1, true);
    setIsLoading(true);
    setShowModal(true);
    setSelectedPortal(null);
  };

  const renderLoadingMoreContent = () => {
    if (!isLoadingMoreContent) {
      return null;
    }
    return (
      <div className="d-flex justify-content-center align-items-center">
        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
      </div>
    );
  };

  const handleSearch = useDebouncedCallback(term => {
    // Clean up items cache and status everytime searchBy changes
    setNextPageToken('');
    setSearchBy(term);
    setIsLoading(true);
  }, 300);

  const confirmChange = callback => {
    const question = 'Are you sure you want to do that? Changes will not be saved.';
    const shouldContinue = window.confirm(question) ? true : false;
    if (shouldContinue) {
      setEditMode(false);
      if (callback) callback();
    }
  };

  const handlePortalSelection = portal => {
    if (editMode) {
      confirmChange(() => {
        setSelectedPortal(portal);
      });
    } else {
      setSelectedPortal(portal);
      setShowModal(false);
    }
  };

  const handlePortalPreview = portal => {
    dispatch(setLoading(true));
    dispatch(startPortalPreview(portal));
    history.replace('/charts');
    window.requestAnimationFrame(() => dispatch(setLoading(false)));
  };

  const handleApiResult = (filter, reload, res) => {
    // Update counter only when querying all siteGroups
    if (filter === '') {
      setTotalPortals(res.totalSize);
    }

    // Reset list when filter changes
    if (filter !== currentFilters || reload) {
      setCurrentFilters(filter);
      setPortals(res.brandPortals);
    } else {
      const newPortals = utils.mergeArraysByField(res.brandPortals, portals, 'id');
      setPortals(newPortals);
    }

    return res.nextPageToken;
  };

  const fetchPortals = async reload => {
    const filter = encodeURI(getFilters());

    if (filter !== currentFilters) {
      setPortals([]);
    }

    return API.getPortals(40, nextPageToken, filter).then(res => {
      return handleApiResult(filter, reload, res);
    });
  };

  const loadMoreItems = async (startIndex, stopIndex, reload = false) => {
    setIsLoadingMoreContent(true);
    paginationUtils.setItemsStatus(startIndex, stopIndex, itemStatusMap.current, LOADING);

    // Perform fetch on parent component
    return fetchPortals(reload).then(newPageToken => {
      paginationUtils.setItemsStatus(startIndex, stopIndex, itemStatusMap.current, LOADED);
      setIsLoading(false);
      setIsLoadingMoreContent(false);
      setNextPageToken(newPageToken);
    });
  };

  const getFilters = () => {
    const searchQuery = searchBy !== '' ? `displayName ILIKE "%${searchBy}%" OR id ILIKE "%${searchBy}%"` : '';
    const ownerQuery = ownerFilter !== 'none' && user && user.id ? `(createdBy = "${user.id}")` : '';

    const hasFilterAndSearch = searchQuery !== '' && ownerQuery !== '' ? ' AND ' : '';

    return `${searchQuery}${hasFilterAndSearch}${ownerQuery}`;
  };

  const cellRenderer = ({ columnIndex, key, rowIndex, style }, columnCount) => {
    const index = rowIndex * columnCount + columnIndex;
    const currentPortal = portals[index];

    if (!currentPortal) return;

    return (
      <div key={key} style={style}>
        <PortalCard
          portal={currentPortal}
          selectedPortal={selectedPortal}
          canPreview={user?.type !== 'BRAND'}
          onPreview={handlePortalPreview}
          onSelect={portal => {
            handlePortalSelection(portal);
          }}
        />
      </div>
    );
  };

  const handleCreatePortal = portal => {
    const { displayName, description, users, features } = portal;
    const isInvitingUsers = users && users.length > 0;
    const body = {
      displayName,
      description,
      companyId: company.id,
      activeFeatures: features || []
    };
    setIsCreateMode(false);
    API.createPortal(body)
      .then(res => {
        setIsLoading(true);
        itemStatusMap.current = {};
        loadMoreItems(0, BATCH_ROW_SIZE - 1, true);
        if (isInvitingUsers) {
          inviteUsers(res.id, users);
        }
        dispatch(setAlert(utils.generateAlert(`Portal created successfully.`, 'success')));
      })
      .catch(err => {
        dispatch(setAlert(utils.generateAlert(`Error creating portal.`, 'error')));
        console.error('Error creating portal:', err);
      })
      .finally(() => {
        setIsCreateMode(false);
        setNewPortal({});
      });
  };

  const inviteUsers = (portalId, users) => {
    if (!portalId) return;
    setIsLoading(true);
    const invitePromises = users.map(user =>
      API.inviteUserToPortal(portalId, { email: user.email, role: 'admobilize.brand' })
    );

    Promise.allSettled(invitePromises)
      .then(results => {
        results.forEach(res => {
          if (res.status === 'rejected') {
            console.error('Error inviting user to portal:', res.reason);
          }
        });
      })
      .catch(error => {
        console.error('Error processing user invitations:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const isButtonDisabled = () => {
    if (company && company.quota && company.quota.maxBrandPortals) {
      if (totalPortals === 0) {
        return false;
      }
      return totalPortals >= company.quota.maxBrandPortals;
    }
  };

  const renderPortalsList = () => {
    if (portals && portals.length === 0) {
      return (
        <div className="no-data-message flex-column">
          <h1>No Portals found. Create your first Portal.</h1>
          <p>If you do have created portals, please contact support </p>
        </div>
      );
    }
    return (
      <div className={`portals-container ${user?.id === selectedPortal?.createdBy ? 'is-admin' : 'not-admin'}`}>
        <div style={{ width: '100%', height: window.innerHeight - CONTAINER_OUTER_ELEMENTS_HEIGHT }}>
          {portals && portals.length > 0 && (
            <AutoSizer>
              {({ width, height }) => {
                const columnCount = computeColumnCount(width);
                const columnWidth = (width - CARD_OUTER_PADDING) / columnCount;
                const rowCount = Math.ceil(portals.length / columnCount);
                const totalRows = Math.ceil(portals.length / columnCount);

                return (
                  <InfiniteLoader
                    isRowLoaded={params => {
                      const lastRowIndex = params.index + 1;
                      // Return false will trigger ´loadMoreRows`. This happen only when there is an empty row (itemStatusMap.current[index] is empty)
                      return lastRowIndex <= totalRows
                        ? paginationUtils.isRowLoaded(lastRowIndex, itemStatusMap.current)
                        : true;
                    }}
                    loadMoreRows={({ startIndex, stopIndex }) => loadMoreItems(startIndex, stopIndex + BATCH_ROW_SIZE)}
                    threshold={5}
                    minimumBatchSize={BATCH_ROW_SIZE}
                    rowCount={rowCount}
                  >
                    {({ onRowsRendered, registerChild }) => (
                      <Grid
                        ref={registerChild}
                        onSectionRendered={params =>
                          onRowsRendered({ startIndex: params.rowStartIndex, stopIndex: params.rowStopIndex })
                        }
                        className={`sitegroups-grid ${selectedPortal ? 'drawer-open' : ''}`}
                        containerStyle={{ overflowY: 'auto' }}
                        cellRenderer={({ columnIndex, key, rowIndex, style }) =>
                          cellRenderer({ columnIndex, key, rowIndex, style }, columnCount)
                        }
                        height={height}
                        width={width}
                        rowCount={rowCount}
                        columnCount={columnCount}
                        rowHeight={ROW_HEIGHT}
                        columnWidth={columnWidth}
                      />
                    )}
                  </InfiniteLoader>
                );
              }}
            </AutoSizer>
          )}
        </div>
        {selectedPortal && (
          <>
            <ManagePortalModal
              portal={selectedPortal}
              isOpen={!showModal}
              onCancel={() => setShowCancelDialog(true)}
              toggle={keepOpen => {
                if (!keepOpen) {
                  setShowCancelDialog(false);
                  setShowModal(false);
                  setSelectedPortal(null);
                }
              }}
              reloadCallback={handleUpdateInfo}
            />
            <CustomConfirm
              messageType={'noModifications'}
              confirmCb={() => {
                setShowCancelDialog(false);
                setShowModal(false);
                setSelectedPortal(null);
              }}
              cancelCb={() => {
                setShowCancelDialog(false);
              }}
              isOpen={showCancelDialog}
            />
          </>
        )}
      </div>
    );
  };

  if (isCreateMode) {
    return (
      <div className="portal-creation">
        <Wizard
          title={'Create a new portal'}
          icon={'projectDarkOutline'}
          steps={createSections(newPortal, setNewPortal, () => {
            /*reladCallback*/
          })}
          onSubmit={() => handleCreatePortal(newPortal)}
          onCancel={() => {
            setIsCreateMode(false);
            setNewPortal({});
            setSelectedPortal(null);
          }}
        />
      </div>
    );
  }

  return (
    <div className="portals">
      <ToolBar
        title="Portals"
        icon="projectDarkOutline"
        searchPlaceholder={'Search portals'}
        count={totalPortals > 0 ? totalPortals : null}
        onSearch={handleSearch}
        renderFilters={
          <>
            <CustomCheckBox
              label={'Owned Portals'}
              selected={ownerFilter}
              onClick={() => {
                setOwnerFilter(ownerFilter !== 'none' ? 'none' : 'all');
                setIsLoading(true);
                setNextPageToken('');
              }}
            />
            {renderLoadingMoreContent()}
          </>
        }
        hasButton
        buttonTitle="Create Portal"
        buttonTooltip={isButtonDisabled() ? 'Portal Limit Reached. Contact Sales team to increase it.' : null}
        disabledButton={isButtonDisabled()}
        buttonPress={() => {
          setIsCreateMode(true);
        }}
      />
      {isLoading || !portals ? <Loading size={15} /> : renderPortalsList()}
    </div>
  );
};

export default PortalsContainer;
